import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Network, FileDown, AlertTriangle, ShieldCheck, Sparkles, ListChecks, Check } from "lucide-react";

const PRI_CLS = {
  P1: "border-red-300 bg-red-50 text-red-700",
  P2: "border-amber-300 bg-amber-50 text-amber-700",
  P3: "border-zinc-300 bg-zinc-50 ek-text-muted",
};

// Per-engagement IT Delivery / DevOps operating-model lens. Derives the org
// context from the engagement, resolves the archetype + a ranked backlog, and
// lets an accredited owner SME-verify catalogue items (global per item).
export default function TomLensPanel({ clientId }) {
  const [result, setResult] = useState(null);
  const [derived, setDerived] = useState({});
  const [isOwner, setIsOwner] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [promoted, setPromoted] = useState([]);
  const [promotingId, setPromotingId] = useState(null);

  const loadPromoted = useCallback(() => {
    api.get(`/admin/delivery-tom/engagements/${clientId}/promoted`)
      .then(({ data }) => setPromoted(data.promoted_item_ids || [])).catch(() => {});
  }, [clientId]);

  const load = useCallback(() => {
    api.get(`/admin/delivery-tom/engagements/${clientId}/context`)
      .then(({ data }) => setDerived(data.derived || {})).catch(() => {});
    api.post("/admin/delivery-tom/resolve", { engagement_id: clientId })
      .then(({ data }) => setResult(data)).catch(() => setResult({ backlog: [] }));
    loadPromoted();
  }, [clientId, loadPromoted]);

  useEffect(() => {
    api.get("/me").then(({ data }) => setIsOwner(data?.platform_role === "founder")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const promote = async (r) => {
    setPromotingId(r.id);
    try {
      const { data } = await api.post("/admin/delivery-tom/promote-to-raid",
        { engagement_id: clientId, item_id: r.id });
      toast.success(data.already_tracked
        ? `Already tracked as ${data.ref} in the RAID register`
        : `Tracked as ${data.ref} in the RAID register`);
      loadPromoted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to RAID");
    } finally { setPromotingId(null); }
  };

  const toggleVerify = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/delivery-tom/items/${r.id}/verify`, { verified: r.unverified });
      toast.success(r.unverified ? "Marked SME-verified" : "Verification removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update verification");
    } finally { setBusyId(null); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.post("/admin/delivery-tom/export.pdf",
        { engagement_id: clientId }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `delivery-tom-sow_${clientId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("TOM SOW starter exported");
    } catch (e) { toast.error("Could not export PDF"); }
    finally { setExporting(false); }
  };

  const rows = result?.backlog || [];
  const grouped = {};
  rows.forEach((r) => { (grouped[r.category_label] = grouped[r.category_label] || []).push(r); });

  return (
    <div className="ek-surface border ek-line rounded-xl p-5 mb-6" data-testid="tom-lens-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h3 className="ek-heading text-base flex items-center gap-2"><Network size={17} /> IT Delivery / DevOps operating model (application lens)</h3>
          <p className="text-[12px] ek-text-muted mt-0.5 max-w-xl">
            Context-derived from this engagement — the recommended operating-model archetype plus a ranked org-design / automation / DevOps / AI backlog. Deterministic &amp; reproducible.
          </p>
        </div>
        <button onClick={exportPdf} disabled={exporting || !rows.length} data-testid="tom-lens-export-pdf"
                title={rows.length ? "Export a branded TOM SOW starter" : "No backlog to export"}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] border ek-line rounded-lg ek-text-soft hover:bg-zinc-50 disabled:opacity-40 shrink-0">
          <FileDown size={14} /> {exporting ? "Exporting…" : "TOM SOW PDF"}
        </button>
      </div>

      {result?.archetype_detail && (
        <div className="rounded-lg border border-violet-300 bg-violet-50 p-3 mb-3" data-testid="tom-lens-archetype">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] flex items-center gap-1.5"><Sparkles size={12} /> Recommended archetype</div>
          <div className="text-[15px] font-semibold ek-text mt-0.5" data-testid="tom-lens-archetype-label">{result.archetype_detail.label}</div>
          <p className="text-[12px] ek-text-soft mt-1 max-w-2xl">{result.archetype_detail.summary}</p>
          {result.drivers?.length ? <p className="text-[11px] ek-text-muted mt-1">Drivers: {result.drivers.join(" · ")}</p> : null}
          <div className="text-[12px] ek-text-muted mt-1.5">
            Backlog: <span className="text-red-700 font-semibold">{result.counts?.P1 || 0} P1</span> · <span className="text-amber-700 font-semibold">{result.counts?.P2 || 0} P2</span> · {result.counts?.P3 || 0} P3
            {" · "}<span className="text-emerald-700 font-semibold">{result.verified_count || 0} verified</span>
          </div>
        </div>
      )}

      {result?.any_unverified && (
        <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11.5px] text-amber-900 flex items-center gap-2 mb-3" data-testid="tom-lens-disclaimer">
          <AlertTriangle size={13} /> {result.disclaimer}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-[12.5px] ek-text-muted py-4" data-testid="tom-lens-empty">
          No operating-model backlog resolved for this engagement yet.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="mb-4">
            <h4 className="ek-text-soft text-[11px] uppercase tracking-[0.14em] mb-2">{cat}</h4>
            <div className="border ek-line rounded-lg divide-y divide-zinc-100">
              {items.map((r) => (
                <div key={r.id} className="p-3" data-testid={`tom-lens-row-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${PRI_CLS[r.priority]}`}>{r.priority}</span>
                    <span className="text-[13px] font-medium ek-text">{r.title}</span>
                    <span className="text-[10px] ek-text-muted border ek-line rounded px-1.5 py-0.5">{r.effort} effort</span>
                    {r.unverified
                      ? <span className="text-[10px] text-amber-700 flex items-center gap-0.5" data-testid={`tom-lens-unverified-${r.id}`}><AlertTriangle size={9} /> unverified</span>
                      : <span className="text-[10px] text-emerald-700 flex items-center gap-0.5" data-testid={`tom-lens-verified-${r.id}`}><ShieldCheck size={9} /> verified</span>}
                  </div>
                  <p className="text-[12px] ek-text-muted mt-1">{r.capability}</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {promoted.includes(r.id) ? (
                      <span className="text-[11px] px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded inline-flex items-center gap-1" data-testid={`tom-lens-tracked-${r.id}`}>
                        <Check size={11} /> In RAID register
                      </span>
                    ) : (
                      <button onClick={() => promote(r)} disabled={promotingId === r.id}
                              data-testid={`tom-lens-promote-${r.id}`}
                              title="Promote this recommendation into the engagement's RAID register as a tracked, owned dependency"
                              className="text-[11px] px-2 py-1 border border-[#7737F6]/40 text-[#7737F6] rounded hover:bg-[#7737F6]/5 disabled:opacity-40 inline-flex items-center gap-1">
                        <ListChecks size={12} /> {promotingId === r.id ? "Adding…" : "Track in RAID"}
                      </button>
                    )}
                    {r.unverified ? (
                      <button onClick={() => toggleVerify(r)} disabled={!isOwner || busyId === r.id}
                              data-testid={`tom-lens-verify-${r.id}`}
                              title={isOwner ? "" : "Only an accredited owner can SME-verify"}
                              className="text-[11px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-40">
                        {busyId === r.id ? "Saving…" : "Mark SME-verified"}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] ek-text-muted">Verified{r.verified_by ? ` by ${r.verified_by}` : ""}</span>
                        <button onClick={() => toggleVerify(r)} disabled={!isOwner || busyId === r.id}
                                data-testid={`tom-lens-unverify-${r.id}`}
                                className="text-[11px] px-2 py-1 border ek-line rounded hover:bg-zinc-50 disabled:opacity-40">
                          {busyId === r.id ? "Saving…" : "Remove verification"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
